import { useMutation } from '@apollo/client/react';
import { type FC, useEffect, useRef, useState } from 'react';

import InlineEditableField from '@zen/Components/InlineEditableField';
import LabelledValue from '@zen/Components/LabelledValue';
import { useNotification } from '@zen/utils/hooks/useNotification';
import type { IOkOrErrorResult } from '@zen/utils/OkOrErrorResult';
import { performMutation } from '@zen/utils/performMutation';

import { UpdateHsCodesDocument, type UpdateHsCodesMutationResult, type UpdateHsCodesMutationVariables } from './graphql';
import { formatHsCodes } from './helper';

interface Props {
  canUpdateHsCodes: boolean;
  cargoId: string;
  hsCodes: string;
}

const HsCodesInlineField: FC<Props> = ({ canUpdateHsCodes, cargoId, hsCodes }) => {
  const [clicked, setClicked] = useState<boolean>(hsCodes.length === 0);
  const [hsCodesValue, setHsCodesValue] = useState<string>(hsCodes);

  const { addSuccess, addError } = useNotification();

  const [updateHsCodes] = useMutation<UpdateHsCodesMutationResult, UpdateHsCodesMutationVariables>(UpdateHsCodesDocument);

  const label: string = 'Tariff codes';

  const ref = useRef<HTMLDivElement>(null);

  const handleBlur = () => {
    setClicked(false);
  };

  const handleClick = () => {
    if (canUpdateHsCodes) {
      setClicked(true);
    }
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setClicked(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (value: string): Promise<IOkOrErrorResult> => {
    if (!value || value.trim().length === 0) {
      addError(`${label} must not be blank`);
      throw Error();
    }

    value.split(',').forEach((hsCode) => {
      if (!hsCode.match(/^\d{6,}$/)) {
        addError(`Tariff code must be a number with at least 6 digits`);
        throw Error();
      }
    });

    return performMutation({
      mutationFn: () =>
        updateHsCodes({
          variables: {
            input: {
              hsCodes: value,
              cargoId
            }
          }
        }),
      onError: () => addError(),
      onSuccess: () => {
        addSuccess(`${label} updated.`);
        setHsCodesValue(value);
        setClicked(false);
      }
    });
  };

  if (!canUpdateHsCodes || !clicked) {
    return (
      <div className={canUpdateHsCodes ? 'cursor-pointer' : ''} data-testid="wrapper-div" onClick={handleClick}>
        <LabelledValue label={label} variant="default">
          {formatHsCodes(hsCodesValue)}
        </LabelledValue>
      </div>
    );
  }

  return (
    // This feels a bit hacky to get this to display correctly due to the ml-3 on the inline editable field label and input.
    <div ref={ref} className="-ml-3" onBlur={handleBlur}>
      <InlineEditableField
        copyable={false}
        label={label}
        name="hs-codes"
        onUpdate={handleChange}
        placeholder="Enter tariff codes"
        value={hsCodesValue}
      />
    </div>
  );
};

export default HsCodesInlineField;
